import { Image, ModalTitle } from '@cian/ui-kit';
import * as React from 'react';
import clsx from 'clsx';

import type { IAiSummaryNewbuilding } from '../../types';

import * as styles from './styles.css';

export interface IContentHeaderProps {
  activeNewbuildingId: number;
  items: IAiSummaryNewbuilding[];
  title: string;
  onSelectNewbuilding: (id: number) => void;
}

export const ContentHeader = ({
  activeNewbuildingId,
  items,
  title,
  onSelectNewbuilding,
}: IContentHeaderProps): React.JSX.Element => {
  const tabsRef = React.useRef<HTMLDivElement>(null);
  const pointerIdRef = React.useRef<number | null>(null);
  const dragRef = React.useRef({ startX: 0, startScrollLeft: 0, moved: false });

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'touch' || event.button > 0) {
      return;
    }

    pointerIdRef.current = event.pointerId;
    dragRef.current = {
      startX: event.clientX,
      startScrollLeft: event.currentTarget.scrollLeft,
      moved: false,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const tabs = tabsRef.current;
    if (!tabs || pointerIdRef.current !== event.pointerId) {
      return;
    }

    const delta = event.clientX - dragRef.current.startX;

    if (Math.abs(delta) <= 4) {
      return;
    }

    if (!tabs.hasPointerCapture(event.pointerId)) {
      tabs.setPointerCapture(event.pointerId);
    }

    dragRef.current.moved = true;
    tabs.scrollLeft = dragRef.current.startScrollLeft - delta;
  };

  const onPointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const tabs = tabsRef.current;

    if (tabs?.hasPointerCapture(event.pointerId)) {
      tabs.releasePointerCapture(event.pointerId);
    }

    pointerIdRef.current = null;
  };

  const onSelect = (id: number) => {
    if (dragRef.current.moved) {
      return;
    }

    onSelectNewbuilding(id);
  };

  return (
    <div className={styles['container']}>
      <ModalTitle>{title}</ModalTitle>
      <div
        className={styles['tabs']}
        data-testid="ai-summary-tabs"
        onDragStartCapture={event => event.preventDefault()}
        onPointerCancelCapture={onPointerEnd}
        onPointerDownCapture={onPointerDown}
        onPointerMoveCapture={onPointerMove}
        onPointerUpCapture={onPointerEnd}
        ref={tabsRef}
      >
        {items.map(item => {
          const isActive = activeNewbuildingId === item.id;

          return (
            <div
              className={clsx(styles['image-wrapper'], isActive && styles['active-boarder'])}
              data-testid={`ai-summary-tab-${item.id}`}
              key={item.id}
              onClick={() => onSelect(item.id)}
            >
              <Image borderRadius="8px" height={48} src={item.imageUrl} width={48} />
            </div>
          );
        })}
      </div>
    </div>
  );
};
